<template>
  <page-with-back-btn title="LayBy Payment">
    <!-- Sale Summary Card -->
    <div class="summary-card card-base mx-3 mb-3">
      <div class="card-header">
        <h3 class="summary-title">
          <font-awesome-icon icon="fa-solid fa-cart-shopping" class="icon"/>
          Sale Summary
        </h3>
      </div>
      <div class="card-body">
        <div class="summary-row">
          <span class="summary-label">Total Amount:</span>
          <span class="summary-value total">{{ currencySymbol }}{{ totalPayment.toFixed(2) }}</span>
        </div>
        <div class="summary-divider"></div>
        <div class="amount-input-group">
          <label for="layby-amount" class="input-label">
            <font-awesome-icon icon="fa-solid fa-money-bill-wave" class="icon"/>
            Initial Amount Paid
          </label>
          <div class="input-wrapper">
            <span class="currency-prefix">{{ currencySymbol }}</span>
            <input
              id="layby-amount"
              type="number"
              step="0.01"
              min="0"
              :max="totalPayment"
              class="form-control amount-input"
              v-model.number="layByDetails.amount"
              :placeholder="`Max: ${currencySymbol}${totalPayment.toFixed(2)}`"
            >
          </div>
          <div class="quick-amounts">
            <button
              v-for="percentage in [25, 50, 75, 100]"
              :key="percentage"
              @click="setQuickAmount(percentage)"
              class="quick-btn"
              :class="{ active: Math.abs(layByDetails.amount - (totalPayment * percentage / 100)) < 0.01 }"
            >
              {{ percentage }}%
            </button>
          </div>
        </div>
        <div v-if="layByDetails.amount > totalPayment" class="error-message">
          <font-awesome-icon icon="fa-solid fa-exclamation-triangle"/>
          Amount cannot exceed total: {{ currencySymbol }}{{ totalPayment.toFixed(2) }}
        </div>
        <div v-if="layByDetails.amount > 0" class="balance-info">
          <font-awesome-icon icon="fa-solid fa-clock" class="icon"/>
          Remaining Balance: {{ currencySymbol }}{{ (totalPayment - layByDetails.amount).toFixed(2) }}
        </div>
      </div>
    </div>

    <!-- Customer Selection Card -->
    <div class="customer-card card-base mx-3 mb-3">
      <div class="card-header">
        <h3 class="card-title">
          <font-awesome-icon icon="fa-solid fa-user" class="icon"/>
          Customer Details
        </h3>
      </div>
      <div class="card-body">
        <div class="form-group">
          <label class="form-label" for="layby-customer">Select Customer</label>
          <div class="customer-selector">
            <div class="form-floating select-container">
              <select
                class="form-select custom-select"
                id="layby-customer"
                v-model="layByDetails.customerID"
                :class="{ 'is-invalid': attemptedSubmit && !layByDetails.customerID }"
              >
                <option value="" disabled>Select an existing customer</option>
                <option v-for="(customer, index) in layByCustomers" :key="index" :value="customer.id">
                  {{ customer.name }} - {{ customer.contact }}
                </option>
              </select>
              <label for="layby-customer">Customer</label>
              <div class="select-arrow">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/>
                </svg>
              </div>
            </div>
            <button class="btn btn-add-customer" @click="createNew" :disabled="loading">
              <font-awesome-icon icon="fa-solid fa-plus"/>
              New
            </button>
          </div>
          <div v-if="attemptedSubmit && !layByDetails.customerID" class="invalid-feedback">
            Please select a customer
          </div>
        </div>
      </div>
    </div>

    <!-- Payment Methods Card -->
    <div class="payment-card card-base mx-3 mb-3">
      <div class="card-header">
        <h3 class="card-title">
          <font-awesome-icon icon="fa-solid fa-credit-card" class="icon"/>
          Payment Method
        </h3>
      </div>
      <div class="card-body">
        <div class="payment-methods-section">
          <!-- Primary Payment Methods -->
          <div class="payment-methods-row">
            <button
              @click="processSale('Cash')"
              class="btn-payment-method"
              :class="'btn-cash'"
              :disabled="!canProcessPayment || processing"
            >
              <div class="payment-icon">
                <font-awesome-icon icon="fa-solid fa-money-bill-wave"/>
              </div>
              <span>Cash</span>
            </button>

            <button
              @click="processSale('Card')"
              class="btn-payment-method"
              :class="'btn-card'"
              :disabled="!canProcessPayment || processing"
            >
              <div class="payment-icon">
                <font-awesome-icon icon="fa-solid fa-credit-card"/>
              </div>
              <span>Card</span>
            </button>

            <button
              @click="processSale('Kazang')"
              class="btn-payment-method"
              :class="'btn-kazang'"
              :disabled="!canProcessPayment || processing"
            >
              <div class="payment-icon">
                <img src="@/assets/img/Kazang.png" alt="Kazang" class="kazang-logo">
              </div>
              <span>Kazang</span>
            </button>
          </div>

          <!-- Additional Payment Methods -->
          <div class="payment-methods-row" v-if="paymentMethods.length > 0">
            <button
              v-for="method in paymentMethods"
              :key="method.name"
              @click="processSale(method.name)"
              class="btn-payment-method"
              :class="method.IsBaseCurrency ? 'btn-foreign-base' : 'btn-foreign'"
              :disabled="!canProcessPayment || processing"
            >
              <ForeignCurrencyLayby
                v-if="!method.IsBaseCurrency"
                :row-data="method"
                @click="processSale(method.name)"
              />
              <span v-else>{{ method.name }} ({{ method.CurrencySymbol }})</span>
            </button>
          </div>
        </div>

        <div v-if="!canProcessPayment && attemptedSubmit" class="error-message text-center">
          <font-awesome-icon icon="fa-solid fa-exclamation-circle"/>
          Please select a customer and enter a valid amount
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="processing" class="loading-overlay">
      <div class="spinner-container">
        <div class="spinner"></div>
        <p class="loading-text">Processing LayBy Payment...</p>
      </div>
    </div>

    <!-- Create Customer Modal -->
    <custom-modal
      ref="laybyCreate"
      title="Create Lay By Customer"
      :is-form="true"
      @submit="createLayByCustomer"
      :submit-loading="creatingCustomer"
      submit-text="Create Customer"
    >
      <div class="customer-form">
        <div class="form-row">
          <div class="form-group">
            <label class="form-label" for="layby-customer-name">Name *</label>
            <input
              id="layby-customer-name"
              type="text"
              required
              class="form-control custom-input"
              v-model="customer.name"
              placeholder="Enter customer name"
              :class="{ 'is-invalid': submitted && !customer.name }"
            >
            <div v-if="submitted && !customer.name" class="invalid-feedback">
              Name is required
            </div>
          </div>

          <div class="form-group">
            <label class="form-label" for="layby-customer-surname">Surname</label>
            <input
              id="layby-customer-surname"
              type="text"
              class="form-control custom-input"
              v-model="customer.surname"
              placeholder="Enter customer surname"
            >
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label" for="layby-customer-contact">Contact No. *</label>
            <input
              id="layby-customer-contact"
              type="text"
              required
              class="form-control custom-input"
              v-model="customer.contact"
              placeholder="Enter contact number"
              :class="{ 'is-invalid': submitted && !customer.contact }"
            >
            <div v-if="submitted && !customer.contact" class="invalid-feedback">
              Contact number is required
            </div>
          </div>

          <div class="form-group">
            <label class="form-label" for="layby-customer-tpin">TPIN</label>
            <input
              id="layby-customer-tpin"
              type="text"
              class="form-control custom-input"
              v-model="customer.tpin"
              placeholder="Enter TPIN (optional)"
            >
          </div>
        </div>

        <div class="form-group">
          <label for="layby-address" class="form-label">Address</label>
          <textarea
            v-model="customer.address"
            class="form-control custom-textarea"
            id="layby-address"
            rows="3"
            placeholder="Enter customer address"
          ></textarea>
        </div>

        <line-separator title="Link to Debtor (Optional)"/>

        <div class="form-group">
          <div class="form-floating select-container">
            <select
              class="form-select custom-select"
              id="layBy_debtor"
              v-model="customer.debtorID"
            >
              <option value="" disabled selected>Select debtor (optional)</option>
              <option v-for="(debtor, index) in $store.getters.getDebtors" :key="index" :value="debtor.Id">
                {{ debtor.Name }}
              </option>
            </select>
            <label for="layBy_debtor">Debtor</label>
            <div class="select-arrow">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </custom-modal>
  </page-with-back-btn>
</template>

<script>
import PageWithBackBtn from "@/components/layout/utils/PageWithBackBtn";
import CustomModal from "@/components/layout/utils/CustomModal";
import LineSeparator from "@/components/layout/utils/LineSeparator";
import ForeignCurrencyLayby from "@/components/point-of-sale/sales/payment/ForeignCurrencyLayby";
import Swal from "sweetalert2";

export default {
  name: "LayByPayment",
  components: {
    ForeignCurrencyLayby,
    LineSeparator,
    CustomModal,
    PageWithBackBtn
  },
  async beforeMount() {
    this.$store.commit(`generateSaleUUID`);
    await Promise.all([
      this.loadLayByCustomers(),
      this.loadDebtors()
    ]);
  },
  computed: {
    paymentMethods() {
      return this.$store.getters.getPaymentMethods
        .filter(method => method.active);
    },
    totalPayment() {
      return Number((
        this.$store.getters.getSaleTotal -
        this.$store.getters.getSaleDiscount.total
      ).toFixed(2));
    },
    currencySymbol() {
      const baseCurrency = this.$store.getters.getBaseCurrency;
      return baseCurrency?.CurrencySymbol || 'K';
    },
    canProcessPayment() {
      return this.layByDetails.customerID &&
             this.layByDetails.amount > 0 &&
             this.layByDetails.amount <= this.totalPayment;
    },
    attemptedSubmit() {
      return this.attemptedSubmitFlag;
    }
  },
  data() {
    return {
      layByCustomers: [],
      loading: false,
      processing: false,
      creatingCustomer: false,
      submitted: false,
      attemptedSubmitFlag: false,
      customer: {
        name: null,
        surname: null,
        contact: null,
        tpin: null,
        debtorID: null,
        address: '',
      },
      layByDetails: {
        customerID: null,
        amount: 0,
      }
    };
  },
  mounted() {
    // Set default amount to 50% of total
    if (this.totalPayment > 0) {
      this.layByDetails.amount = Number((this.totalPayment * 0.5).toFixed(2));
    }
  },
  methods: {
    async loadLayByCustomers() {
      this.loading = true;
      try {
        const obj = { name: "" };
        this.layByCustomers = await this.$store.dispatch(`searchLayByCustomers`, obj);
      } catch (error) {
        console.error('Error loading LayBy customers:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load customers. Please try again.'
        });
      } finally {
        this.loading = false;
      }
    },
    createNew() {
      this.submitted = false;
      this.customer = {
        name: null,
        surname: null,
        contact: null,
        tpin: null,
        debtorID: null,
        address: '',
      };
      return this.$refs.laybyCreate.openModal();
    },
    async createLayByCustomer() {
      this.submitted = true;
      this.creatingCustomer = true;

      if (!this.customer.name || !this.customer.contact) {
        Swal.fire({
          icon: 'warning',
          title: 'Validation Error',
          text: 'Please fill in all required fields'
        });
        this.creatingCustomer = false;
        return;
      }

      try {
        await this.$store.dispatch(`createLayByCustomer`, this.customer);
        await this.loadLayByCustomers();
        this.$refs.laybyCreate.closeModal();
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Customer created successfully',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error creating customer:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to create customer. Please try again.'
        });
      } finally {
        this.creatingCustomer = false;
      }
    },
    async loadDebtors() {
      try {
        await this.$store.dispatch(`getAllDebtors`, {
          Name: "",
          tpin: "",
          contact: ""
        });
      } catch (error) {
        console.error('Error loading debtors:', error);
      }
    },
    setQuickAmount(percentage) {
      const amount = Number((this.totalPayment * percentage / 100).toFixed(2));
      this.layByDetails.amount = amount;
    },
    async processSale(paymentMethod) {
      this.attemptedSubmitFlag = true;

      if (!this.canProcessPayment) {
        return;
      }

      if (paymentMethod === `Kazang`) {
        return this.processKazangPayment();
      }

      try {
        this.processing = true;
        const saleDetails = this.$store.getters.getSaleDetails;
        saleDetails.type = paymentMethod;
        // Ensure total is set in saleDetails for parseInvoice
        saleDetails.total = this.totalPayment;
        this.$store.dispatch(`setCustomer`, this.layByDetails.customerID);

        const saleData = await this.$store.dispatch('parseInvoice', saleDetails);
        saleData.paidAmount = Number(this.layByDetails.amount);

        await this.$store.dispatch(`processLayByPayment`, saleData);

        await Swal.fire({
          icon: `success`,
          title: 'LayBy Created Successfully',
          text: `Initial payment of ${this.currencySymbol}${this.layByDetails.amount.toFixed(2)} received`,
          timer: 2000,
          showConfirmButton: false
        });

        await this.$router.push({ name: 'pos' });
      } catch (error) {
        console.error('Error processing LayBy payment:', error);
        Swal.fire({
          icon: 'error',
          title: 'Payment Failed',
          text: error.message || 'An error occurred while processing the payment'
        });
      } finally {
        this.processing = false;
      }
    },
    async processKazangPayment() {
      try {
        const result = await Swal.fire({
          title: 'Kazang Payment',
          text: 'Enter customer phone number:',
          icon: 'question',
          input: 'tel',
          inputPlaceholder: '09XXXXXXXXX',
          showCancelButton: true,
          confirmButtonText: 'Process',
          allowOutsideClick: false,
          inputValidator: (value) => {
            if (!value || value.length < 9) {
              return 'Please enter a valid phone number';
            }
          }
        });

        if (result.isConfirmed && result.value) {
          this.processing = true;
          const response = await this.$store.dispatch('firstKazangPaymentStepLayBy', result.value);

          if (response && !response.Error) {
            await Swal.fire({
              icon: `success`,
              title: 'LayBy Created',
              text: 'Kazang payment processed successfully',
              timer: 2000,
              showConfirmButton: false
            });
            await this.$router.push({ name: 'pos' });
          }
        }
      } catch (error) {
        console.error('Error processing Kazang payment:', error);
        Swal.fire({
          icon: 'error',
          title: 'Payment Failed',
          text: error.message || 'Failed to process Kazang payment'
        });
      } finally {
        this.processing = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
/* Base Styles */
.card-base {
  background: var(--card-bg, #FFFFFF);
  border: 1px solid var(--card-border, #E2E8F0);
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  }
}

.card-header {
  padding: 20px 24px;
  background: linear-gradient(135deg, rgba(var(--accent-electric-blue-rgb), 0.05) 0%, rgba(var(--accent-neon-purple-rgb), 0.05) 100%);
  border-bottom: 1px solid var(--card-border, #E2E8F0);

  .card-title, .summary-title {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--text-primary);
    display: flex;
    align-items: center;
    gap: 10px;

    .icon {
      color: var(--accent-electric-blue);
      font-size: 1.2rem;
    }
  }
}

.card-body {
  padding: 24px;
}

/* Summary Card */
.summary-card {
  .summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .summary-label {
      font-size: 1rem;
      color: var(--text-secondary);
      font-weight: 500;
    }

    .summary-value {
      font-size: 1.5rem;
      font-weight: 700;

      &.total {
        color: var(--accent-electric-blue);
      }
    }
  }

  .summary-divider {
    height: 1px;
    background: var(--card-border, #E2E8F0);
    margin: 20px 0;
  }

  .amount-input-group {
    .input-label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 500;
      margin-bottom: 12px;
      color: var(--text-primary);
      font-size: 0.95rem;

      .icon {
        color: var(--accent-electric-blue);
      }
    }

    .input-wrapper {
      position: relative;
      display: flex;
      align-items: center;

      .currency-prefix {
        position: absolute;
        left: 16px;
        font-weight: 600;
        color: var(--text-secondary);
        z-index: 2;
      }

      .amount-input {
        padding-left: 40px;
        height: 56px;
        font-size: 1.3rem;
        font-weight: 600;
        border-radius: 12px;
        border: 2px solid var(--card-border, #E2E8F0);
        background-color: var(--bg-primary);
        color: var(--text-primary);
        transition: all 0.3s ease;

        &:focus {
          border-color: var(--accent-electric-blue);
          box-shadow: 0 0 0 4px rgba(var(--accent-electric-blue-rgb), 0.1);
          outline: none;
        }
      }
    }

    .quick-amounts {
      display: flex;
      gap: 8px;
      margin-top: 12px;

      .quick-btn {
        flex: 1;
        padding: 8px 12px;
        border: 1px solid var(--card-border, #E2E8F0);
        background: var(--bg-primary);
        border-radius: 8px;
        font-size: 0.9rem;
        font-weight: 500;
        color: var(--text-secondary);
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
          border-color: var(--accent-electric-blue);
          color: var(--accent-electric-blue);
          background: rgba(var(--accent-electric-blue-rgb), 0.05);
        }

        &.active {
          background: var(--accent-electric-blue);
          color: white;
          border-color: var(--accent-electric-blue);
        }
      }
    }
  }

  .error-message {
    margin-top: 12px;
    padding: 12px;
    background: rgba(var(--danger-rgb), 0.1);
    border: 1px solid var(--danger);
    border-radius: 8px;
    color: var(--danger);
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .balance-info {
    margin-top: 12px;
    padding: 12px 16px;
    background: rgba(var(--accent-neon-purple-rgb), 0.1);
    border-radius: 8px;
    color: var(--accent-neon-purple);
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;

    .icon {
      font-size: 1rem;
    }
  }
}

/* Customer Card */
.customer-card {
  .customer-selector {
    display: flex;
    gap: 12px;
    align-items: flex-end;

    .select-container {
      flex: 1;
    }

    .btn-add-customer {
      background: var(--accent-electric-blue);
      color: white;
      border: none;
      border-radius: 12px;
      padding: 14px 20px;
      font-size: 0.95rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      height: 56px;
      min-width: 90px;

      &:hover:not(:disabled) {
        background: var(--accent-electric-blue-dark);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(var(--accent-electric-blue-rgb), 0.3);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }
}

/* Payment Card */
.payment-card {
  .payment-methods-section {
    .payment-methods-row {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      gap: 12px;
      margin-bottom: 16px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .btn-payment-method {
      width: 100%;
      padding: 20px 16px;
      border: none;
      border-radius: 12px;
      font-weight: 600;
      font-size: 0.95rem;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 10px;
      min-height: 100px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

      .payment-icon {
        font-size: 1.8rem;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .kazang-logo {
        height: 32px;
        width: auto;
      }

      &:hover:not(:disabled) {
        transform: translateY(-3px);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
      }

      /* Payment Method Colors */
      &.btn-cash {
        background: linear-gradient(135deg, #FF9500, #FFB340);
        color: white;

        &:hover:not(:disabled) {
          background: linear-gradient(135deg, #E68600, #EEA030);
        }
      }

      &.btn-card {
        background: linear-gradient(135deg, #32D74B, #5EE06B);
        color: white;

        &:hover:not(:disabled) {
          background: linear-gradient(135deg, #28B33D, #4DD45C);
        }
      }

      &.btn-kazang {
        background: linear-gradient(135deg, #A529FF, #BD4AFF);
        color: white;

        &:hover:not(:disabled) {
          background: linear-gradient(135deg, #8C20D6, #A73BE6);
        }
      }

      &.btn-foreign-base {
        background: var(--bg-primary);
        color: var(--text-primary);
        border: 2px solid var(--card-border);

        &:hover:not(:disabled) {
          border-color: var(--accent-electric-blue);
          background: rgba(var(--accent-electric-blue-rgb), 0.05);
        }
      }

      &.btn-foreign {
        background: linear-gradient(135deg, var(--accent-neon-purple), #BD4AFF);
        color: white;

        &:hover:not(:disabled) {
          background: linear-gradient(135deg, #8C20D6, #A73BE6);
        }
      }
    }
  }
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);

  .spinner-container {
    background: var(--card-bg);
    padding: 40px;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;

    .spinner {
      width: 50px;
      height: 50px;
      border: 4px solid rgba(var(--accent-electric-blue-rgb), 0.2);
      border-top-color: var(--accent-electric-blue);
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    .loading-text {
      margin: 0;
      color: var(--text-primary);
      font-weight: 500;
      font-size: 1rem;
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Form Controls */
.form-group {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }

  .form-label {
    display: block;
    font-weight: 500;
    margin-bottom: 8px;
    color: var(--text-primary);
    font-size: 0.9rem;
  }

  .custom-input, .custom-select, .custom-textarea {
    width: 100%;
    padding: 12px 16px;
    border: 2px solid var(--card-border, #E2E8F0);
    border-radius: 12px;
    background-color: var(--bg-primary);
    color: var(--text-primary);
    transition: all 0.3s ease;
    font-size: 0.95rem;
    font-family: 'Montserrat', sans-serif;

    &:focus {
      border-color: var(--accent-electric-blue);
      box-shadow: 0 0 0 4px rgba(var(--accent-electric-blue-rgb), 0.1);
      outline: none;
    }

    &.is-invalid {
      border-color: var(--danger);
    }

    &::placeholder {
      color: var(--text-secondary);
    }
  }

  .custom-textarea {
    resize: vertical;
    min-height: 80px;
  }
}

.invalid-feedback {
  display: block;
  margin-top: 6px;
  font-size: 0.85rem;
  color: var(--danger);
}

/* Customer Form */
.customer-form {
  .form-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
    margin-bottom: 20px;
  }
}

/* Select Styling */
.select-container {
  position: relative;

  .select-arrow {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: var(--accent-electric-blue);
    transition: transform 0.3s ease;
  }

  .form-select.custom-select {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    cursor: pointer;
    height: 56px;
    padding-right: 40px;

    &:focus + .select-arrow svg {
      transform: rotate(180deg);
    }
  }
}

.form-floating label {
  padding: 0.75rem 1rem;
  color: var(--text-secondary);
  font-weight: 500;
  pointer-events: none;
}

/* Text Center */
.text-center {
  text-align: center;
}

/* Responsive Design */
@media (max-width: 768px) {
  .card-header {
    padding: 16px 20px;

    .card-title, .summary-title {
      font-size: 1rem;
    }
  }

  .card-body {
    padding: 20px;
  }

  .customer-selector {
    flex-direction: column;
    align-items: stretch;

    .btn-add-customer {
      width: 100%;
    }
  }

  .payment-methods-row {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .btn-payment-method {
    min-height: 80px;
    font-size: 0.9rem;
  }

  .customer-form .form-row {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

@media (max-width: 480px) {
  .card-body {
    padding: 16px;
  }

  .summary-card .summary-value {
    font-size: 1.3rem;
  }

  .amount-input-group .amount-input {
    height: 48px;
    font-size: 1.1rem;
  }

  .btn-payment-method {
    min-height: 70px;
  }
}
</style>
